
import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Comments } from "@/components/social/Comments";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { MessageSquare, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CommentsDialogProps {
  derivativeId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  disabled?: boolean; // Added disabled prop to conditionally render dialog
}

export const CommentsDialog = ({
  derivativeId,
  isOpen,
  onOpenChange,
  title = "Comments",
  disabled = false // Default to false to maintain backward compatibility
}: CommentsDialogProps) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  
  // Reset scroll position when dialog opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const contentElement = document.querySelector('[data-comments-content]');
        if (contentElement) {
          contentElement.scrollTop = 0;
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Don't render anything if disabled is true
  if (disabled) {
    return null;
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange} modal={isMobile}>
      <SheetContent 
        side={isMobile ? "bottom" : "right"}
        className="flex flex-col w-full sm:max-w-md overflow-hidden p-0 shadow-lg bg-background/95 backdrop-blur-sm"
      >
        <SheetHeader className="px-4 py-3 border-b sticky top-0 z-10 bg-background/90 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-foreground/70" />
              <SheetTitle className="text-base">{title || t('common.comments')}</SheetTitle>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-8 w-8 rounded-full opacity-70 hover:opacity-100"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </SheetHeader>
        
        <div 
          className="flex-1 overflow-hidden" 
          data-comments-content
        >
          <Comments 
            derivativeId={derivativeId} 
            maxHeight={isMobile ? "60vh" : "calc(100vh - 80px)"} 
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};
